//* Libraries imports
import { useState } from "react";
import { motion } from "framer-motion";
import { CheckCircle, XCircle, Circle, Eye } from "lucide-react";

//* Components imports
import { CriteriaDetailsDialog } from "./criteria-details-dialog";

//* Types imports
import { FullResult } from "../../../../schemas/common/result/result";

//* Styles imports
import "./view-result-card-question.scss";

interface AlternativeProps {
  alternativa: {
    id: number;
    name: string;
    letra: string;
  };
  isCorrect: boolean;
  isSelected: boolean;
  questionId: number;
}

function Alternative({ 
  alternativa, 
  isCorrect, 
  isSelected 
}: AlternativeProps) {

  const getAlternativeClass = () => {
    if (isSelected && isCorrect) {
      return "alternative-item correct-selected";
    } else if (isSelected && !isCorrect) {
      return "alternative-item incorrect-selected";
    } else if (!isSelected && isCorrect) {
      return "alternative-item correct-not-selected";
    }
    return "alternative-item default";
  };

  const getIcon = () => {
    if (isSelected && isCorrect) {
      return <CheckCircle size={20} />;
    } else if (isSelected && !isCorrect) {
      return <XCircle size={20} />;
    } else if (!isSelected && isCorrect) {
      return <CheckCircle size={20} />;
    }
    return <Circle size={20} />;
  };

  return (
    <motion.div 
      className={getAlternativeClass()}
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.3 }}
      whileHover={{ scale: 1.01 }}
    >
      <div className="letter-circle">
        {alternativa.letra})
      </div>
      <div 
        className="alternative-text"
        dangerouslySetInnerHTML={{ __html: alternativa.name }}
      />
      <div className="status-icon">
        {getIcon()}
      </div>
    </motion.div>
  );
}

interface QuestionProps {
  questao: FullResult["resultado"]["questoes"][0];
  questionNumber: number;
  userAnswer: string | null | undefined;
  showAnswers: boolean;
  examId?: number; 
}

export function ViewResultCardQuestion({ 
  questao, 
  questionNumber, 
  userAnswer, 
  showAnswers,
  examId 
}: QuestionProps) {
  const [showCriteriaDialog, setShowCriteriaDialog] = useState(false);
    // Only access correta property for question types that have it
  const isCorrect = questao.type_questao === "simples" && 'correta' in questao
    ? userAnswer === questao.correta
    : false; 
  
    const isEssayQuestion = questao.type_questao === "dissertativa";
  const shouldShowCriteriaButton = questao.type_questao === "dissertativa";
  const isAnswered = isEssayQuestion ? Boolean(userAnswer) : Boolean(userAnswer);
  const getQuestionTypeLabel = () => {
    switch (questao.type_questao) {
      case "simples":
        return "Objetiva";
      case "dissertativa":
        return "Dissertativa";
      case "conjunto":
        return "Texto em Conjunto";
      default:
        const _exhaustiveCheck: never = questao;
        return _exhaustiveCheck;
    }
  };
  return (
    <>
    <motion.div 
      className="question-card"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
    ><div className="question-card-header">
        <div className="header-content">
          <h2 className="question-title">
            Questão {questionNumber} - {getQuestionTypeLabel()}
          </h2>
          <div className="header-right">
            <span className="question-category">{questao.categoria.name}</span>
            {isEssayQuestion && (
              <span className={`answer-status ${isAnswered ? 'answered' : 'not-answered'}`}>
                {isAnswered ? '✓ Respondida' : '⚠ Não respondida'}
              </span>
            )}            
            {/* Eye icon for dissertation and conjunto questions to view criteria */}
            {shouldShowCriteriaButton && ('correcao_dissertativa' in questao) && questao.correcao_dissertativa && (
              <motion.button
                className="criteria-details-btn"
                onClick={() => setShowCriteriaDialog(true)}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
                title="Ver critérios de correção detalhados"
                aria-label="Ver critérios de correção detalhados"
              >
                <Eye size={20} />
              </motion.button>
            )}
          </div>
        </div>
      </div>
      
      <div className="question-card-body">
        <div 
          className="question-content"
          dangerouslySetInnerHTML={{ __html: questao.name }}
        />        
        {questao.type_questao === "dissertativa" && (
          <motion.div 
            className="essay-answer"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
          >
            <h3 className="essay-title">Sua Resposta:</h3>
            <div className={`essay-content ${!userAnswer ? 'empty-response' : ''}`}>
              {userAnswer ? (
                <div className="essay-text">
                  {userAnswer}
                </div>
              ) : (
                <div className="empty-message">
                  <span className="empty-icon">📝</span>
                  <span>Esta questão não foi respondida</span>
                </div>
              )}
            </div>
            {userAnswer && (
              <div className="essay-info">
                <small className="text-muted">
                  Resposta registrada: {userAnswer.length} caracteres
                </small>
              </div>
            )}
            
            {/* Essay correction info */}
            {questao.correcao_dissertativa && (
              <motion.div 
                className="essay-correction"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.3 }}
              >
                <h4 className="correction-title">Correção:</h4>
                <div className="correction-info">
                  <div className="correction-status">
                    <span className={`status-badge ${questao.correcao_dissertativa.foi_corrigida ? 'corrected' : 'pending'}`}>
                      {questao.correcao_dissertativa.foi_corrigida ? '✓ Corrigida' : '⏳ Pendente'}
                    </span>
                    {questao.correcao_dissertativa.foi_corrigida && (
                      <span className="grade">
                        Nota: {questao.correcao_dissertativa.nota_total}
                      </span>
                    )}
                  </div>
                  {questao.correcao_dissertativa.comentario_geral && (
                    <div className="correction-comment">
                      <strong>Comentário do corretor:</strong>
                      <p>{questao.correcao_dissertativa.comentario_geral}</p>
                    </div>
                  )}
                </div>
              </motion.div>
            )}
          </motion.div>        )}        
        {/* Simple objective questions */}
        {questao.type_questao === "simples" && 'alternativas' in questao && questao.alternativas && (
          <div className="multiple-choice">
            {showAnswers && questao.correta && (
              <motion.div 
                className="answer-info"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.1 }}
              >
                <div className="answer-row">
                  <div className="answer-item correct-answer-item">
                    <span className="answer-label">Resposta correta:</span>
                    <span className="correct-answer">{questao.correta}</span>
                  </div>
                  <div className="answer-separator"></div>
                  <div className="answer-item user-answer-item">
                    <span className="answer-label">Sua resposta:</span>
                    <span className="user-answer">{userAnswer || "Não respondida"}</span>
                    {userAnswer && (
                      <span className={`status-badge ${isCorrect ? "correct" : "incorrect"}`}>
                        {isCorrect ? "✓ Correta" : "✗ Incorreta"}
                      </span>
                    )}
                  </div>
                </div>
              </motion.div>
            )}

            <h3 className="alternatives-title">Alternativas:</h3>
            <motion.div 
              className="alternatives-container"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2 }}
            >
              {questao.alternativas.map((alternativa, index) => (
                <motion.div
                  key={alternativa.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.1 * index }}
                >
                  <Alternative
                    alternativa={alternativa}
                    isCorrect={questao.correta ? alternativa.letra === questao.correta : false}
                    isSelected={alternativa.letra === userAnswer}
                    questionId={questao.id}
                  />
                </motion.div>              
              ))}
            </motion.div>
          </div>
        )}        
        
        {/* Conjunto questions */}
        {questao.type_questao === "conjunto" && 'questoes' in questao && (
          <div className="conjunto-questions">
            <h3 className="conjunto-title">Questões do Conjunto:</h3>
            <div className="conjunto-questions-list">
              {questao.questoes.map((subQuestao, subIndex) => {
                const subQuestionNumber = `${questionNumber}.${subIndex + 1}`;
                const isSubQuestionCorrect = subQuestao.resposta_estudante === subQuestao.correta;
                
                return (
                  <motion.div
                    key={subQuestao.id}
                    className="sub-question-card"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 * subIndex }}
                  >
                    <div className="sub-question-header">
                      <h4 className="sub-question-title">
                        Questão {subQuestionNumber}
                      </h4>
                      <div className="sub-question-info">
                        <span className="question-category">
                          {subQuestao.categoria.name}
                        </span>
                        {showAnswers && subQuestao.correta && (
                          <div className="sub-question-answer">
                            <span className={`answer-status ${isSubQuestionCorrect ? 'correct' : 'incorrect'}`}>
                              {isSubQuestionCorrect ? '✓ Correto' : '✗ Incorreto'}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>

                    <div 
                      className="sub-question-content"
                      dangerouslySetInnerHTML={{ __html: subQuestao.name }}
                    />

                    {showAnswers && subQuestao.correta && (
                      <motion.div 
                        className="answer-info"
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: 0.1 }}
                      >
                        <div className="answer-row">
                          <div className="answer-item correct-answer-item">
                            <span className="answer-label">Resposta correta:</span>
                            <span className="correct-answer">{subQuestao.correta}</span>
                          </div>
                          <div className="answer-separator"></div>
                          <div className="answer-item user-answer-item">
                            <span className="answer-label">Sua resposta:</span>
                            <span className="user-answer">{subQuestao.resposta_estudante || "Não respondida"}</span>
                            {subQuestao.resposta_estudante && (
                              <span className={`status-badge ${isSubQuestionCorrect ? "correct" : "incorrect"}`}>
                                {isSubQuestionCorrect ? "✓ Correta" : "✗ Incorreta"}
                              </span>
                            )}
                          </div>
                        </div>
                      </motion.div>
                    )}

                    <div className="sub-question-alternatives">
                      <h5 className="alternatives-title">Alternativas:</h5>
                      <motion.div 
                        className="alternatives-container"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.2 }}
                      >
                        {subQuestao.alternativas.map((alternativa, altIndex) => (
                          <motion.div
                            key={alternativa.id}
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: 0.1 * altIndex }}
                          >
                            <Alternative
                              alternativa={alternativa}
                              isCorrect={subQuestao.correta ? alternativa.letra === subQuestao.correta : false}
                              isSelected={alternativa.letra === subQuestao.resposta_estudante}
                              questionId={subQuestao.id}
                            />
                          </motion.div>
                        ))}
                      </motion.div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </motion.div>      
    
    {/* Criteria Details Dialog */}
    <CriteriaDetailsDialog
      show={showCriteriaDialog}
      onHide={() => setShowCriteriaDialog(false)}
      examId={examId}
      questaoId={questao.id}
      isEssayQuestion={isEssayQuestion}
    />
    </>
  );
}